// components/Navigation.js
import React, { useState, useEffect } from 'react';
import { Menu, X, Sun, Moon, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { translations, languages } from '../utils/translations';

export const Navigation = ({ darkMode, toggleDarkMode, language, onLanguageChange }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const handleLanguageChange = (languageCode) => {
    onLanguageChange(languageCode);
    setIsLanguageMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const t = translations[language].nav;

  const navLinks = [
    { label: t.home, id: 'hero' },
    { label: t.services, id: 'services' },
    { label: t.projects, id: 'projects' },
    { label: t.about, id: 'about' },
    { label: t.contact, id: 'contact' },
  ];

  const currentLang = languages.find(lang => lang.code === language);

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-md shadow-lg border-b border-gray-200/50 dark:border-gray-700/50' 
          : darkMode
            ? 'bg-transparent'
            : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <button
            onClick={() => scrollToSection('hero')}
            className={`text-xl md:text-2xl font-bold transition-colors duration-300 ${
              isScrolled
                ? 'text-gray-800 dark:text-white hover:text-blue-600 dark:hover:text-blue-400'
                : darkMode
                  ? 'text-white hover:text-blue-400'
                  : 'text-gray-800 hover:text-blue-600'
            }`}
          >
            Triarani<span className="text-blue-600 dark:text-blue-400">.</span>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className={`font-medium text-sm transition-all duration-300 ${
                  isScrolled
                    ? 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
                    : darkMode
                      ? 'text-white hover:text-blue-400'
                      : 'text-gray-800 hover:text-blue-600'
                }`}
              >
                {link.label}
              </button>
            ))}

            {/* Language Selector - Desktop */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                className={`flex items-center gap-2 p-2 rounded-full transition-all duration-300 ${
                  isScrolled
                    ? 'hover:bg-gray-200 dark:hover:bg-gray-700'
                    : darkMode
                      ? 'hover:bg-white/20'
                      : 'hover:bg-black/10'
                }`}
                aria-label="Change language"
              >
                <Globe size={18} className={
                  isScrolled 
                    ? 'text-gray-600 dark:text-gray-300' 
                    : darkMode 
                      ? 'text-white' 
                      : 'text-gray-700'
                } />
                <span className={`text-sm font-medium ${
                  isScrolled
                    ? 'text-gray-700 dark:text-gray-300'
                    : darkMode
                      ? 'text-white'
                      : 'text-gray-800'
                }`}>
                  {currentLang.flag}
                </span>
              </button>

              <AnimatePresence>
                {isLanguageMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`flex items-center gap-3 w-full px-4 py-2 text-left transition-colors duration-200 ${
                          language === lang.code
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <span className="text-base">{lang.flag}</span>
                        <span className="text-sm font-medium">{lang.name}</span>
                        {language === lang.code && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full ml-auto"></div>
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Dark Mode Toggle - Desktop */}
            <button
              onClick={toggleDarkMode}
              className={`p-2 rounded-full transition-all duration-300 ${
                isScrolled
                  ? 'hover:bg-gray-200 dark:hover:bg-gray-700'
                  : darkMode
                    ? 'hover:bg-white/20'
                    : 'hover:bg-black/10'
              }`}
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {darkMode ? (
                <Sun className={isScrolled ? "text-yellow-400" : "text-yellow-300"} size={20} />
              ) : (
                <Moon className={isScrolled ? "text-gray-700" : "text-gray-800"} size={20} />
              )}
            </button>

            <button
              onClick={() => scrollToSection('contact')}
              className={`px-6 py-2.5 rounded-lg transition-all duration-300 font-medium text-sm ${
                isScrolled
                  ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md'
                  : darkMode
                    ? 'bg-blue-500 hover:bg-blue-400 text-white shadow-lg'
                    : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg'
              }`}
            >
              {t.getStarted}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center md:hidden space-x-3">
            {/* Language Selector - Mobile */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                className={`p-2 rounded-full transition-all duration-300 ${
                  isScrolled
                    ? 'hover:bg-gray-200 dark:hover:bg-gray-700'
                    : darkMode
                      ? 'hover:bg-white/20'
                      : 'hover:bg-black/10'
                }`}
                aria-label="Change language"
              >
                <span className={`text-sm font-medium ${
                  isScrolled
                    ? 'text-gray-700 dark:text-gray-300'
                    : darkMode
                      ? 'text-white'
                      : 'text-gray-800'
                }`}>
                  {currentLang.flag}
                </span>
              </button>

              <AnimatePresence>
                {isLanguageMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full right-0 mt-2 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`flex items-center gap-3 w-full px-4 py-2 text-left transition-colors duration-200 ${
                          language === lang.code
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <span className="text-base">{lang.flag}</span>
                        <span className="text-sm font-medium">{lang.name}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Dark Mode Toggle - Mobile */}
            <button
              onClick={toggleDarkMode}
              className={`p-2 rounded-full transition-all duration-300 ${
                isScrolled
                  ? 'hover:bg-gray-200 dark:hover:bg-gray-700'
                  : darkMode
                    ? 'hover:bg-white/20'
                    : 'hover:bg-black/10'
              }`}
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {darkMode ? (
                <Sun className={isScrolled ? "text-yellow-400" : "text-yellow-300"} size={20} />
              ) : (
                <Moon className={isScrolled ? "text-gray-700" : "text-gray-800"} size={20} />
              )}
            </button>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 rounded transition-all duration-300 ${
                isScrolled
                  ? 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  : darkMode
                    ? 'text-white hover:bg-white/20'
                    : 'text-gray-800 hover:bg-black/10'
              }`}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className={`md:hidden overflow-hidden ${
              isScrolled
                ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md'
                : darkMode
                  ? 'bg-gray-900/95 backdrop-blur-md'
                  : 'bg-white/95 backdrop-blur-md'
            }`}
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className={`block w-full text-left transition-colors duration-300 font-medium py-3 text-base ${
                    isScrolled
                      ? 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
                      : darkMode
                        ? 'text-white hover:text-blue-400'
                        : 'text-gray-800 hover:text-blue-600'
                  }`}
                >
                  {link.label}
                </button>
              ))}

              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={toggleDarkMode}
                  className={`flex items-center w-full text-left py-3 transition-colors duration-300 text-base ${
                    isScrolled
                      ? 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
                      : darkMode
                        ? 'text-white hover:text-blue-400'
                        : 'text-gray-800 hover:text-blue-600'
                  }`}
                >
                  {darkMode ? (
                    <>
                      <Sun className={`mr-3 ${isScrolled ? "text-yellow-400" : "text-yellow-300"}`} size={20} />
                      {t.lightMode}
                    </>
                  ) : (
                    <>
                      <Moon className={`mr-3 ${isScrolled ? "text-gray-700" : "text-gray-800"}`} size={20} />
                      {t.darkMode}
                    </>
                  )}
                </button>

                <button
                  onClick={() => scrollToSection('contact')}
                  className={`w-full mt-4 px-6 py-3 rounded-lg transition-all duration-300 font-medium text-base ${
                    isScrolled
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : darkMode
                        ? 'bg-blue-500 hover:bg-blue-400 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {t.getStarted}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navigation;